import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';
import { VISIONX_CONSTANTS } from 'src/app/ComponentMap';
import {
  ChangePasswordRequestDTO,
  QrCodeRequestDTO,
  QrCodeResponseDTO,
  QrCodeVerifyRequestDTO,
} from 'src/pages/login/types';
import { environmentService } from 'src/providers/environment/EnvironmentService';
import { AUTH_REASON_KEY, guslStorage } from 'src/providers/session/GuslStorage';
import { isRefreshTokenExpiredFromTokenLogin } from 'src/providers/session/helpers/authErrors';
import { buildReturnUrl, buildLoginUrl } from 'src/providers/session/helpers/buildLoginUrl';
import {
  GuslUser,
  JWTTokens,
  JwtTokenSignInDTO,
  SessionStatus,
  SignInResponseDTO,
} from 'src/providers/session/types';
import { HttpClient } from './useHttpClient';

export interface SessionAuthDeps {
  httpClient: HttpClient;
  loginSubject: BehaviorSubject<GuslUser | null>;
  systemReadySubject: BehaviorSubject<boolean>;
  sessionStatusSubject: BehaviorSubject<SessionStatus>;
  setPermissions: React.Dispatch<React.SetStateAction<string[]>>;
  setOriginalPath: (path: string) => void;
}

export interface SessionAuth {
  hasNoSession: () => boolean;
  preSignIn: (username: string, password: string) => Promise<SignInResponseDTO>;
  signIn: (
    username: string,
    password: string,
    totp?: string,
    session?: string,
    challenge?: string,
  ) => Promise<SignInResponseDTO>;
  signIWithNewPassword: (
    username: string,
    password: string,
    session: string,
    challenge?: string,
  ) => Promise<SignInResponseDTO>;
  signOut: () => Promise<{ success: boolean }>;
  forgotPassword: (username: string) => Promise<SignInResponseDTO>;
  getQrCode: (username: string) => Promise<QrCodeResponseDTO>;
  verifyTotp: (code: string) => Promise<SignInResponseDTO>;
  forceLogout: (reason?: string) => void;
  signInWithJwtToken: (jwt?: JWTTokens) => Promise<SignInResponseDTO>;
  performSessionValidation: () => Promise<boolean>;
}

export function useSessionAuth({
  httpClient,
  loginSubject,
  systemReadySubject,
  sessionStatusSubject,
  setPermissions,
  setOriginalPath,
}: SessionAuthDeps): SessionAuth {
  const navigate = useNavigate();

  const convertToGuslUser = (response: SignInResponseDTO): GuslUser => ({ ...response });

  const forceLogout = React.useCallback(
    (reason?: string) => {
      setPermissions([]);
      loginSubject.next(null);
      systemReadySubject.next(false);
      sessionStatusSubject.next(SessionStatus.INVALID);

      if (reason) sessionStorage.setItem(AUTH_REASON_KEY, reason);

      const returnUrl = buildReturnUrl();
      setOriginalPath(returnUrl);
      guslStorage.clearSession();

      navigate(buildLoginUrl(), { replace: true });
    },
    [
      navigate,
      loginSubject,
      systemReadySubject,
      sessionStatusSubject,
      setPermissions,
      setOriginalPath,
    ],
  );

  /** Module-level deduplication for token-login requests */
  let inFlightTokenLogin: Promise<SignInResponseDTO> | null = null;

  const signInWithJwtToken = React.useCallback(
    async (jwt?: JWTTokens): Promise<SignInResponseDTO> => {
      if (!jwt) return Promise.reject('Missing JWT tokens');
      if (!jwt.refreshToken) {
        forceLogout('Your session expired. Please sign in again.');
        return Promise.reject('Missing refresh token');
      }

      // Deduplicate concurrent token-login calls
      if (inFlightTokenLogin) {
        return inFlightTokenLogin;
      }

      const request: JwtTokenSignInDTO = {
        accessToken: jwt.accessToken,
        refreshToken: jwt.refreshToken,
        idToken: jwt.idToken,
        sessionToken: jwt.sessionToken,
        userToken: jwt.userToken,
      };

      inFlightTokenLogin = (async () => {
        try {
          const response = await httpClient.post<JwtTokenSignInDTO, SignInResponseDTO>(
            '/security/v1/token-login',
            request,
          );

          guslStorage.setJwtSession({
            accessToken: response.data.accessToken,
            idToken: response.data.idToken,
            refreshToken: jwt.refreshToken,
            sessionToken: jwt.sessionToken,
            userToken: jwt.userToken,
          });

          if (response.data.permissions) setPermissions(response.data.permissions.permissions);

          environmentService.setHomePage(response.data.homepage);
          environmentService.setSiemType(response.data.siemType);
          environmentService.setAppMode(response.data.applicationMode);
          environmentService.setNamingMapping(response.data.namingMapping);
          environmentService.setTHDisplay(response.data.threatHubDisplay);
          environmentService.setMttrDisplay(response.data.mttrDisplay);
          environmentService.setGetVizDisplay(response.data.getVisibilityEnabled);
          environmentService.setWizDisplay(response.data.wizFeatureEnabled);
          environmentService.setSplunkMttxDisplay(response.data.splunkMTTXFeatureEnabled);
          environmentService.setDefenderSecureScoreDisplay(response.data.defenderFeatureEnabled);
          environmentService.setNoPhishFeatureEnabled(response.data.noPhishFeatureEnabled);
          environmentService.setTenantShortName(response.data.tenantShortName);
          environmentService.setTimeToIdle(response.data.timeToIdle);
          environmentService.setJiraIncidentsFeatureEnabled(
            response.data.jiraIncidentsFeatureEnabled,
          );
          environmentService.setCisoTrainingFeature(response.data.cisoTrainingFeature);
          environmentService.setSentinelOneFeatureEnabled(response.data.sentinelOneFeatureEnabled);
          environmentService.setSentinelMttxFeatureEnabled(
            response.data.sentinelMttxFeatureEnabled,
          );

          return response.data;
        } catch (err) {
          if (isRefreshTokenExpiredFromTokenLogin(err)) {
            forceLogout('Your session expired. Please sign in again.');
          }
          throw err;
        } finally {
          inFlightTokenLogin = null;
        }
      })();

      return inFlightTokenLogin;
    },
    [forceLogout, httpClient, setPermissions],
  );

  const validateSession = async (): Promise<boolean> => {
    try {
      const jwt = guslStorage.getJwtSession();
      if (!jwt) return false;
      const response = await signInWithJwtToken(jwt);
      loginSubject.next(convertToGuslUser(response));
      if (response.permissions) setPermissions(response.permissions.permissions);
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Finalizes a session after a successful sign-in/pre-sign-in without making
   * an additional token-login network call. Emits the user on loginSubject and
   * updates session/system-ready state.
   */
  const finalizeSession = (response: SignInResponseDTO): void => {
    loginSubject.next(convertToGuslUser(response));
    sessionStatusSubject.next(SessionStatus.VALID);
    systemReadySubject.next(true);
  };

  const performSessionValidation = async (): Promise<boolean> => {
    try {
      const success = await validateSession();
      sessionStatusSubject.next(success ? SessionStatus.VALID : SessionStatus.INVALID);
      systemReadySubject.next(true);
      return success;
    } catch {
      sessionStatusSubject.next(SessionStatus.INVALID);
      systemReadySubject.next(true);
      return false;
    }
  };

  const hasNoSession = () => !guslStorage.getJwtSession();

  const preSignIn = async (username: string, password: string) => {
    const form = new URLSearchParams({ user: username, password });
    const response = await httpClient.postSignIn<URLSearchParams, SignInResponseDTO>(
      '/security/v1/pre-sign-in',
      form,
    );

    const existingSession = guslStorage.getJwtSession();
    if (response.data.totpSetupRequired && response.data.accessToken) {
      guslStorage.clearSession();
      guslStorage.setEnrolmentSession({ accessToken: response.data.accessToken });
    } else {
      guslStorage.setJwtSession({
        ...existingSession,
        accessToken: response.data.accessToken || existingSession?.accessToken,
      });
    }

    if (
      !response.data.totpCodeRequired &&
      !response.data.totpSetupRequired &&
      !response.data.passwordChangeRequired
    ) {
      guslStorage.setJwtSession({
        idToken: response.data.idToken,
        refreshToken: response.data.refreshToken,
        accessToken: response.data.accessToken,
      });
      if (response.data.permissions) setPermissions(response.data.permissions.permissions);

      environmentService.setHomePage(response.data.homepage);
      environmentService.setSiemType(response.data.siemType);
      environmentService.setAppMode(response.data.applicationMode);
      environmentService.setNamingMapping(response.data.namingMapping);
      environmentService.setTHDisplay(response.data.threatHubDisplay);
      environmentService.setMttrDisplay(response.data.mttrDisplay);
      environmentService.setGetVizDisplay(response.data.getVisibilityEnabled);
      environmentService.setWizDisplay(response.data.wizFeatureEnabled);
      environmentService.setSplunkMttxDisplay(response.data.splunkMTTXFeatureEnabled);
      environmentService.setDefenderSecureScoreDisplay(response.data.defenderFeatureEnabled);
      environmentService.setNoPhishFeatureEnabled(response.data.noPhishFeatureEnabled);
      environmentService.setTenantShortName(response.data.tenantShortName);
      environmentService.setTimeToIdle(response.data.timeToIdle);
      environmentService.setJiraIncidentsFeatureEnabled(response.data.jiraIncidentsFeatureEnabled);
      environmentService.setCisoTrainingFeature(response.data.cisoTrainingFeature);
      environmentService.setSentinelOneFeatureEnabled(response.data.sentinelOneFeatureEnabled);
      environmentService.setSentinelMttxFeatureEnabled(response.data.sentinelMttxFeatureEnabled);

      finalizeSession(response.data);
    }

    if (response.data.totpSetupRequired)
      environmentService.setTotpSetup(response.data.totpSetupRequired);
    if (response.data.homepage) environmentService.setHomePage(response.data.homepage);

    return response.data;
  };

  const signIn = async (
    username: string,
    password: string,
    totp?: string,
    session?: string,
    challenge?: string,
  ) => {
    const form = new URLSearchParams({ user: username, password });
    if (totp) form.append('totp', totp);
    if (session) form.append('session', session);
    if (challenge) form.append('challenge', challenge);

    const response = await httpClient.postSignIn<URLSearchParams, SignInResponseDTO>(
      '/security/v1/sign-in',
      form,
    );

    if (response.data.totpSetupRequired && response.data.accessToken) {
      guslStorage.clearSession();
      guslStorage.setEnrolmentSession({ accessToken: response.data.accessToken });
      environmentService.setTotpSetup(true);
      return response.data;
    }

    guslStorage.setJwtSession({
      idToken: response.data.idToken,
      refreshToken: response.data.refreshToken,
      accessToken: response.data.accessToken,
    });

    if (response.data.permissions) setPermissions(response.data.permissions.permissions);
    if (response.data.totpSetupRequired)
      environmentService.setTotpSetup(response.data.totpSetupRequired);
    if (response.data.homepage) environmentService.setHomePage(response.data.homepage);

    environmentService.setSiemType(response.data.siemType);
    environmentService.setAppMode(response.data.applicationMode);
    environmentService.setNamingMapping(response.data.namingMapping);
    environmentService.setTHDisplay(response.data.threatHubDisplay);
    environmentService.setMttrDisplay(response.data.mttrDisplay);
    environmentService.setGetVizDisplay(response.data.getVisibilityEnabled);
    environmentService.setWizDisplay(response.data.wizFeatureEnabled);
    environmentService.setSplunkMttxDisplay(response.data.splunkMTTXFeatureEnabled);
    environmentService.setDefenderSecureScoreDisplay(response.data.defenderFeatureEnabled);
    environmentService.setNoPhishFeatureEnabled(response.data.noPhishFeatureEnabled);
    environmentService.setTenantShortName(response.data.tenantShortName);
    environmentService.setTimeToIdle(response.data.timeToIdle);
    environmentService.setJiraIncidentsFeatureEnabled(response.data.jiraIncidentsFeatureEnabled);
    environmentService.setCisoTrainingFeature(response.data.cisoTrainingFeature);
    environmentService.setSentinelOneFeatureEnabled(response.data.sentinelOneFeatureEnabled);
    environmentService.setSentinelMttxFeatureEnabled(response.data.sentinelMttxFeatureEnabled);

    finalizeSession(response.data);
    return response.data;
  };

  const signIWithNewPassword = async (
    username: string,
    password: string,
    session: string,
    challenge?: string,
  ) => {
    const form = new URLSearchParams({ user: username, password, session });
    if (challenge) form.append('challenge', challenge);

    const response = await httpClient.postSignIn<URLSearchParams, SignInResponseDTO>(
      '/security/v1/sign-in',
      form,
    );

    if (response.data.totpSetupRequired && response.data.accessToken) {
      guslStorage.clearSession();
      guslStorage.setEnrolmentSession({ accessToken: response.data.accessToken });
      environmentService.setTotpSetup(true);
      return response.data;
    }

    guslStorage.setJwtSession({
      idToken: response.data.idToken,
      refreshToken: response.data.refreshToken,
      accessToken: response.data.accessToken,
      sessionToken: response.data.sessionToken,
    });

    if (response.data.permissions) setPermissions(response.data.permissions.permissions);
    if (response.data.totpSetupRequired)
      environmentService.setTotpSetup(response.data.totpSetupRequired);
    if (response.data.homepage) environmentService.setHomePage(response.data.homepage);

    environmentService.setSiemType(response.data.siemType);
    environmentService.setAppMode(response.data.applicationMode);
    environmentService.setNamingMapping(response.data.namingMapping);
    environmentService.setTHDisplay(response.data.threatHubDisplay);
    environmentService.setMttrDisplay(response.data.mttrDisplay);
    environmentService.setGetVizDisplay(response.data.getVisibilityEnabled);
    environmentService.setWizDisplay(response.data.wizFeatureEnabled);
    environmentService.setSplunkMttxDisplay(response.data.splunkMTTXFeatureEnabled);
    environmentService.setDefenderSecureScoreDisplay(response.data.defenderFeatureEnabled);
    environmentService.setNoPhishFeatureEnabled(response.data.noPhishFeatureEnabled);
    environmentService.setTenantShortName(response.data.tenantShortName);
    environmentService.setTimeToIdle(response.data.timeToIdle);
    environmentService.setJiraIncidentsFeatureEnabled(response.data.jiraIncidentsFeatureEnabled);
    environmentService.setCisoTrainingFeature(response.data.cisoTrainingFeature);
    environmentService.setSentinelOneFeatureEnabled(response.data.sentinelOneFeatureEnabled);
    environmentService.setSentinelMttxFeatureEnabled(response.data.sentinelMttxFeatureEnabled);

    finalizeSession(response.data);
    return response.data;
  };

  const signOut = async () => {
    const refreshToken = guslStorage.getJwtSession()?.refreshToken || '';
    const formData = new URLSearchParams({ refreshToken });

    try {
      const response = await httpClient.postSignOut<URLSearchParams, { success: boolean }>(
        '/security/v1/sign-out',
        formData,
      );
      systemReadySubject.next(false);
      return response.data;
    } finally {
      guslStorage.clearSession();
      setOriginalPath('');
      navigate(VISIONX_CONSTANTS.LOGIN_PATH);
      sessionStorage.setItem(AUTH_REASON_KEY, '');
    }
  };

  const forgotPassword = async (username: string): Promise<SignInResponseDTO> => {
    const request: ChangePasswordRequestDTO = { username };
    const response = await httpClient.post<ChangePasswordRequestDTO, SignInResponseDTO>(
      '/user/forgot/change-password',
      request,
    );
    return response.data;
  };

  const getQrCode = async (username: string) => {
    const request: QrCodeRequestDTO = {
      username,
      accessToken: guslStorage.getEnrolmentSession()?.accessToken ?? undefined,
    };
    const response = await httpClient.post<QrCodeRequestDTO, QrCodeResponseDTO>(
      '/user/change-password/qrcode',
      request,
    );
    return response.data;
  };

  const verifyTotp = async (code: string) => {
    const enrolmentSession = guslStorage.getEnrolmentSession();
    if (!enrolmentSession?.accessToken) {
      return Promise.reject({
        data: { message: 'Your setup session has expired. Please sign in again.' },
      });
    }
    const request: QrCodeVerifyRequestDTO = {
      code,
      accessToken: enrolmentSession.accessToken,
    };
    const response = await httpClient.post<QrCodeVerifyRequestDTO, SignInResponseDTO>(
      '/user/qrcode-validate',
      request,
    );
    guslStorage.setJwtSession({
      idToken: response.data.idToken,
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    });
    if (response.data.permissions) setPermissions(response.data.permissions.permissions);
    environmentService.setHomePage(response.data.homepage);
    environmentService.setSiemType(response.data.siemType);
    environmentService.setAppMode(response.data.applicationMode);
    environmentService.setNamingMapping(response.data.namingMapping);
    environmentService.setTHDisplay(response.data.threatHubDisplay);
    environmentService.setMttrDisplay(response.data.mttrDisplay);
    environmentService.setGetVizDisplay(response.data.getVisibilityEnabled);
    environmentService.setWizDisplay(response.data.wizFeatureEnabled);
    environmentService.setSplunkMttxDisplay(response.data.splunkMTTXFeatureEnabled);
    environmentService.setDefenderSecureScoreDisplay(response.data.defenderFeatureEnabled);
    environmentService.setNoPhishFeatureEnabled(response.data.noPhishFeatureEnabled);
    environmentService.setTenantShortName(response.data.tenantShortName);
    environmentService.setTimeToIdle(response.data.timeToIdle);
    environmentService.setJiraIncidentsFeatureEnabled(response.data.jiraIncidentsFeatureEnabled);
    environmentService.setCisoTrainingFeature(response.data.cisoTrainingFeature);
    environmentService.setSentinelOneFeatureEnabled(response.data.sentinelOneFeatureEnabled);
    environmentService.setSentinelMttxFeatureEnabled(response.data.sentinelMttxFeatureEnabled);
    guslStorage.clearEnrolmentSession();
    finalizeSession(response.data);
    return response.data;
  };

  return {
    hasNoSession,
    preSignIn,
    signIn,
    signIWithNewPassword,
    signOut,
    forgotPassword,
    getQrCode,
    verifyTotp,
    forceLogout,
    signInWithJwtToken,
    performSessionValidation,
  };
}
